import { PaintBrush } from '@phosphor-icons/react';
import { useEffect, useState } from 'react';
import {
    connectToEngineSocket,
    disconnectFromEngineSocket,
    type McrDeployResult,
} from '../../../mcr/signalRConnection';
import { getRuntimesForTenant } from '../../../sources/runtime';
import translations from '../../../translations';
import type { Environment } from '../../../types/environment';
import type { ReleaseAPI, ReleaseElementAPI } from '../../../types/releases';
import { isReleaseFlowAPI } from '../../../types/releases';
import type { TenantRuntime } from '../../../types/Tenant';
import type { FlowSecurityInfo } from '../../../utils/flow';
import { isPublic } from '../../../utils/flow';
import { isNullOrEmpty, isNullOrUndefined } from '../../../utils/guard';
import { stringReplace } from '../../../utils/string';
import ButtonIcon from '../../buttons/ButtonIcon';
import { SecurityIcon } from '../../flow/SecurityIcon';
import CopyableText from '../../generic/CopyableText';
import FormGroup from '../../generic/FormGroup';
import ConfirmModal from '../../generic/modal/ConfirmModal';
import Modal from '../../generic/modal/GenericModal';
import Table, { type TableColumnList } from '../../generic/Table';
import FlowIcon from '../../icons/FlowIcon';
import Loader from '../../loader/Loader';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import { ENVIRONMENT_CATEGORY_TYPES } from './constants';
import { useEnvironments } from './EnvironmentsProvider';

interface Props {
    release: ReleaseAPI;
    reload: () => void;
    environmentId: string | null;
    showRollback: boolean;
    passedEnvironments: Environment[];
}

const Release = ({ release, reload, environmentId, showRollback, passedEnvironments }: Props) => {
    const [show, toggle] = useState(false);
    const [loadedRelease, setRelease] = useState<ReleaseAPI | null>(null);
    const [showDeployModal, setShowDeployModal] = useState(false);
    const [environment, setCurrentEnvironment] = useState<Environment | null>(null);
    const [environmentToDeploy, setEnvironmentToDeploy] = useState<Environment | null>(null);
    const [deployed, setDeployed] = useState(false);
    const [rolledBack, setRolledBack] = useState(false);
    const [deployButtonHidden, setDeployButtonHidden] = useState(false);
    const [showRollbackModal, setShowRollbackModal] = useState(false);
    const [removeItemModalVisible, setRemoveItemModalVisible] = useState(false);
    const [itemToRemove, setItemToRemove] = useState<ReleaseElementAPI | null>(null);
    const [allowedToDelete, setAllowedToDelete] = useState(false);
    const [showDeleteReleaseModal, setShowDeleteReleaseModal] = useState(false);
    const [deploying, setDeploying] = useState(false);
    const [runtimes, setRuntimes] = useState<TenantRuntime[]>([]);

    const {
        releaseLoading,
        getRelease,
        deploy,
        notifyRuntimesOnDeploy,
        rollback,
        environments,
        removeFlowFromRelease,
        removeThemeFromRelease,
        deleteRelease,
        getAllEnvironments,
    } = useEnvironments();

    const dispatchNotification = useNotificationDispatch();

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor to add all dependencies
    useEffect(() => {
        const currentEnvironment = Object.values(passedEnvironments ?? environments).find(
            (env) => env.id === release?.currentEnvironmentId,
        );

        loadRuntimes();

        setCurrentEnvironment(currentEnvironment ?? null);
    }, []);

    const toggleView = async () => {
        const visible = !show;
        toggle(visible);

        if (visible) {
            const rel = await getRelease(release.id);
            setRelease(rel);

            const currentEnvironment = Object.values(passedEnvironments ?? environments).find(
                (env) => env.id === rel?.currentEnvironmentId,
            );
            setCurrentEnvironment(currentEnvironment ?? null);

            if (
                !currentEnvironment ||
                currentEnvironment.classificationType === ENVIRONMENT_CATEGORY_TYPES.development
            ) {
                setAllowedToDelete(true);
            }

            const nextEnvironment = Object.values(passedEnvironments ?? environments).find(
                (environment) => environment.id === currentEnvironment?.nextEnvironmentId,
            );

            if (
                (environmentId && environmentId === currentEnvironment?.id && nextEnvironment) ||
                (isNullOrEmpty(environmentId) && nextEnvironment)
            ) {
                setDeployButtonHidden(false);
                setEnvironmentToDeploy(nextEnvironment);
            } else {
                setDeployButtonHidden(true);
            }
        }
    };

    const loadRuntimes = async () => {
        const runtimes = (await getRuntimesForTenant()) as TenantRuntime[];
        setRuntimes(runtimes);
        return runtimes;
    };

    const removeItem = async () => {
        if (itemToRemove) {
            if (isReleaseFlowAPI(itemToRemove)) {
                await removeFlowFromRelease(release.id, itemToRemove.flowId);
            } else {
                await removeThemeFromRelease(release.id, itemToRemove.themeId, itemToRemove.name);
            }
        }

        await reload();
    };

    const onDeleteRelease = async () => {
        await deleteRelease(release.id);
        reload();
    };

    async function reloadWithEnvironments() {
        await getAllEnvironments();
        await reload();
    }

    async function deployRelease() {
        if (environmentToDeploy && loadedRelease) {
            setDeploying(true);
            if (runtimes.length > 0) {
                const messageId: string = crypto.randomUUID();
                const { connectionReady, result } = connectToEngineSocket();

                // Wait for connection to be ready before sending notification
                await connectionReady;

                await notifyRuntimesOnDeploy(
                    messageId,
                    loadedRelease.id,
                    environmentToDeploy.id,
                    false,
                );

                let deployResult: McrDeployResult | null = null;
                try {
                    deployResult = await result;

                    if (deployResult.messageId !== messageId) {
                        throw new Error('Returned Multi Cloud Runtime message ID does not match');
                    }
                } catch (error) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: { error },
                    });
                } finally {
                    if (deployResult?.success) {
                        setDeployed(true);
                    }
                    await disconnectFromEngineSocket();
                }
            } else {
                await deploy(loadedRelease.id, environmentToDeploy.id);
                setDeployed(true);
            }

            setDeploying(false);
        }
    }

    async function rollbackRelease() {
        if (loadedRelease && environment) {
            setDeploying(true);

            if (runtimes.length > 0) {
                const messageId: string = crypto.randomUUID();
                const { connectionReady, result } = connectToEngineSocket();

                // Wait for connection to be ready before sending notification
                await connectionReady;

                await notifyRuntimesOnDeploy(messageId, loadedRelease.id, environment.id, true);

                let deployResult: McrDeployResult | null = null;
                try {
                    deployResult = await result;

                    if (deployResult.messageId !== messageId) {
                        throw new Error('Returned Multi Cloud Runtime message ID does not match');
                    }
                } catch (error) {
                    dispatchNotification({
                        type: 'notifyError',
                        payload: { error },
                    });
                } finally {
                    if (deployResult?.success) {
                        setRolledBack(true);
                    }
                    await disconnectFromEngineSocket();
                }
            } else {
                await rollback(
                    loadedRelease.id,
                    isNullOrEmpty(environmentId) ? environment.id : environmentId,
                );
                setRolledBack(true);
            }
            setDeploying(false);
        }
    }

    const columns: TableColumnList<ReleaseElementAPI> = [
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item }) => (
                <ButtonIcon
                    className="margin-left-small danger"
                    glyph="Delete"
                    aria-label={`Remove ${item.name}`}
                    onClick={() => {
                        setItemToRemove(item);
                        setRemoveItemModalVisible(true);
                    }}
                    title={`Remove ${item.name}`}
                    hidden={deployButtonHidden}
                    iconClass="icon-medium"
                />
            ),
            size: '80px',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_type,
            renderCell: ({ item }) => {
                if (isReleaseFlowAPI(item)) {
                    return <FlowIcon width="20" height="20" />;
                }
                return <PaintBrush width="20" height="20" />;
            },
        },
        {
            renderHeader: () => translations.COMMON_TABLE_name,
            renderCell: ({ item }) => item.name,
        },
        {
            renderHeader: () => translations.COMMON_TABLE_version_id,
            renderCell: ({ item }) => {
                if (isReleaseFlowAPI(item)) {
                    const flowSecurityInfo: FlowSecurityInfo = {
                        authorization: { globalAuthenticationType: item.globalAuthenticationType },
                        identityProvider: item.identityProvider,
                    };

                    const isFlowPublic = isPublic(flowSecurityInfo);
                    const testId = isFlowPublic
                        ? `auth-public-${item.flowId}`
                        : `auth-secured-${item.flowId}`;

                    return (
                        <div className="flex gap-x-small align-center">
                            <SecurityIcon
                                isPublic={isFlowPublic}
                                testId={testId}
                                label={
                                    isFlowPublic
                                        ? translations.FLOW_auth_public_label
                                        : translations.FLOW_auth_secured_label
                                }
                            />
                            <CopyableText
                                copyableText={item.versionId}
                                hasCopyButton={true}
                                wrapperClassName="overflow-hidden"
                            />
                        </div>
                    );
                }

                return <CopyableText copyableText={item.versionId} hasCopyButton={true} />;
            },
        },
        {
            renderHeader: () => translations.COMMON_TABLE_deployment_notes,
            renderCell: ({ item }) => item.summary,
            size: '300px',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_last_modified,
            renderCell: ({ item }) => {
                return item.updatedAt ? new Date(item.updatedAt).toLocaleString() : '';
            },
        },
    ];

    let content = <></>;
    if (loadedRelease) {
        let rows = loadedRelease.flows as ReleaseElementAPI[];
        if (!isNullOrUndefined(loadedRelease.themes)) {
            if (isNullOrUndefined(loadedRelease.flows)) {
                rows = loadedRelease.themes as ReleaseElementAPI[];
            } else {
                rows = rows.concat(loadedRelease.themes as ReleaseElementAPI[]);
            }
        }
        content = (
            <>
                <ConfirmModal
                    show={removeItemModalVisible}
                    title={translations.ENVIRONMENT_remove_item_confirmation_title}
                    messages={[
                        stringReplace(translations.ENVIRONMENT_remove_item_confirmation_message, {
                            itemName: itemToRemove?.name ?? '',
                            releaseName: release?.name ?? '',
                        }),
                    ]}
                    buttonStyle="danger"
                    buttonCaption="Remove"
                    onCancel={() => setRemoveItemModalVisible(false)}
                    onConfirm={() => {
                        removeItem();
                        setRemoveItemModalVisible(false);
                    }}
                />

                <ConfirmModal
                    show={showDeleteReleaseModal}
                    title={translations.ENVIRONMENT_delete_release}
                    messages={[
                        stringReplace(
                            translations.ENVIRONMENT_delete_release_confirmation_message,
                            {
                                releaseName: release?.name ?? '',
                            },
                        ),
                    ]}
                    buttonStyle="danger"
                    buttonCaption="Delete"
                    onCancel={() => setShowDeleteReleaseModal(false)}
                    onConfirm={() => {
                        onDeleteRelease();
                        setShowDeleteReleaseModal(false);
                    }}
                />
                <Table columns={columns} items={rows} isLoading={releaseLoading} />
                {!deployButtonHidden && environmentToDeploy ? (
                    <button
                        type="button"
                        className="btn btn-primary outcome margin-right"
                        onClick={() => setShowDeployModal(!showDeployModal)}
                    >
                        {translations.ENVIRONMENT_promote + environmentToDeploy.name}
                    </button>
                ) : null}
                {showRollback ? (
                    <button
                        type="button"
                        className="btn btn-default outcome"
                        onClick={() => setShowRollbackModal(!showRollbackModal)}
                    >
                        {translations.ENVIRONMENT_rollback}
                    </button>
                ) : null}
                {allowedToDelete ? (
                    <button
                        type="button"
                        className="btn btn-default outcome"
                        onClick={() => setShowDeleteReleaseModal(!showRollbackModal)}
                    >
                        {translations.ENVIRONMENT_delete_release}
                    </button>
                ) : null}
                <Modal
                    show={showDeployModal}
                    onHide={() => setShowDeployModal(!showDeployModal)}
                    title={
                        deployed
                            ? translations.ENVIRONMENT_promote_release_success
                            : translations.ENVIRONMENT_promote_release
                    }
                    className="config-modal"
                    renderBody={() =>
                        deployed ? (
                            <p>
                                {[
                                    stringReplace(
                                        translations.ENVIRONMENT_promote_release_message,
                                        {
                                            releaseName: release?.name ?? '',
                                            environmentName: environmentToDeploy?.name ?? '',
                                        },
                                    ),
                                ]}
                            </p>
                        ) : deploying ? (
                            <Loader message="Deploying release" />
                        ) : (
                            <>
                                <p>
                                    {[
                                        stringReplace(translations.ENVIRONMENT_promote_subtext, {
                                            releaseName: release?.name ?? '',
                                        }),
                                    ]}
                                </p>
                                <FormGroup
                                    label={translations.ENVIRONMENT_promote_target_environment}
                                    htmlFor="deployment-environment"
                                >
                                    <input
                                        id="deployment-environment"
                                        className="form-control form-control-dynamic"
                                        value={environmentToDeploy ? environmentToDeploy.name : ''}
                                        type="text"
                                        readOnly
                                    />
                                </FormGroup>
                            </>
                        )
                    }
                    renderFooter={() =>
                        !deployed && environmentToDeploy ? (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-default outcome"
                                    onClick={() => setShowDeployModal(!showDeployModal)}
                                    disabled={deploying}
                                >
                                    {translations.ENVIRONMENT_cancel}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-primary outcome"
                                    onClick={() => deployRelease()}
                                    disabled={deploying}
                                >
                                    {translations.ENVIRONMENT_promote + environmentToDeploy.name}
                                </button>
                            </>
                        ) : (
                            <button
                                type="button"
                                className="btn btn-primary outcome"
                                onClick={() => reloadWithEnvironments()}
                            >
                                {translations.ENVIRONMENT_finish}
                            </button>
                        )
                    }
                />
                <Modal
                    show={showRollbackModal}
                    onHide={() => setShowRollbackModal(!showRollbackModal)}
                    title={translations.ENVIRONMENT_rollback_release}
                    bodyClassName="modal-body-no-height"
                    renderBody={() =>
                        deploying ? (
                            <Loader message="Rolling back release" />
                        ) : (
                            <p>
                                {rolledBack
                                    ? `${loadedRelease.name} was successfully rolled back from ${environment?.name}`
                                    : translations.ENVIRONMENT_rollback_release_confirmation +
                                      release.name}
                            </p>
                        )
                    }
                    className="config-modal"
                    renderFooter={() =>
                        rolledBack ? (
                            <button
                                type="button"
                                className="btn btn-primary outcome"
                                onClick={() => reloadWithEnvironments()}
                            >
                                {translations.ENVIRONMENT_finish}
                            </button>
                        ) : (
                            <>
                                <button
                                    type="button"
                                    className="btn btn-default outcome"
                                    onClick={() => setShowRollbackModal(!showRollbackModal)}
                                    disabled={deploying}
                                >
                                    {translations.ENVIRONMENT_cancel}
                                </button>

                                <button
                                    type="button"
                                    className="btn btn-danger outcome"
                                    onClick={() => rollbackRelease()}
                                    disabled={deploying}
                                >
                                    {translations.ENVIRONMENT_rollback}
                                </button>
                            </>
                        )
                    }
                />
            </>
        );
    }

    return (
        <div className="expandable-area margin-bottom margin-right-sml">
            {/** biome-ignore lint/a11y/useSemanticElements: Requires dedicated refactor to use correct element */}
            <div
                className="expandable-header margin-left"
                onClick={toggleView}
                onKeyDown={toggleView}
                role="button"
                tabIndex={0}
            >
                <div className="inline-block">
                    <h5 className="inline-block margin-right">{`${release.name}  `}</h5>
                    {release.lastDeployed
                        ? `Last deployed: ${new Date(release.lastDeployed).toLocaleString()} to: ${
                              environment?.name ?? ENVIRONMENT_CATEGORY_TYPES.development
                          }`
                        : 'Not deployed'}
                </div>
                <span
                    className={
                        show
                            ? 'float-right padding padding-top glyphicon glyphicon-triangle-bottom rotate180'
                            : 'float-right padding padding-top glyphicon glyphicon-triangle-bottom'
                    }
                    aria-hidden="true"
                />
            </div>
            <div className={show ? 'expand-show padding-default' : 'expand-hide'}>{content}</div>
        </div>
    );
};

export default Release;
